import { useState, useEffect } from 'react';
import axios from 'axios';
import { Building, Users, FileText, Building2, Mail, Activity, Wrench, Loader2, CheckCircle2, Eye, AlertTriangle, Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

const ROLE_COLORS = {
  super_admin: 'text-red-400',
  admin: 'text-purple-400',
  manager: 'text-blue-400',
  member: 'text-emerald-400',
  viewer: 'text-slate-400',
};

export default function DevDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dev/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  const cards = [
    { label: 'Tenants', value: stats?.total_tenants || 0, icon: Building, color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { label: 'Users', value: stats?.total_users || 0, icon: Users, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
    { label: 'Claims', value: stats?.total_claims || 0, icon: FileText, color: 'text-amber-400', bg: 'bg-amber-400/10' },
    { label: 'Companies', value: stats?.total_companies || 0, icon: Building2, color: 'text-purple-400', bg: 'bg-purple-400/10' },
    { label: 'Pending Invites', value: stats?.pending_invites || 0, icon: Mail, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  ];

  return (
    <div className="p-8" data-testid="dev-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Developer Dashboard</h1>
        <p className="text-sm text-slate-400 mt-1">Platform overview and recent activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
              <div className={`${bg} p-1.5 rounded`}>
                <Icon className={`w-3.5 h-3.5 ${color}`} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Recent Users */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg mb-6">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400" />
          <h2 className="text-sm font-semibold text-white">Recent Signups</h2>
        </div>
        <div className="divide-y divide-slate-800">
          {(stats?.recent_users || []).map((u) => (
            <div key={u.user_id} className="px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-medium text-slate-300">
                    {u.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-200">{u.name}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${ROLE_COLORS[u.role] || 'text-slate-400'}`}>
                  {u.role?.replace('_', ' ')}
                </span>
                <span className="text-[10px] text-slate-600">
                  {u.auth_provider === 'google' ? 'Google' : 'Email'}
                </span>
              </div>
            </div>
          ))}
          {(!stats?.recent_users || stats.recent_users.length === 0) && (
            <p className="px-5 py-4 text-sm text-slate-500 text-center">No users yet</p>
          )}
        </div>
      </div>

      {/* Data Maintenance */}
      <DataMaintenance />
    </div>
  );
}

function DataMaintenance() {
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});
  const [orphans, setOrphans] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);

  const actions = [
    {
      id: 'fix-statuses',
      group: 'Claims',
      label: 'Fix Claim Statuses',
      desc: 'Normalize type_data status from "Open" to "New" for all claims',
      endpoint: '/dev/fix-claim-statuses',
      color: 'text-amber-400',
      bg: 'bg-amber-400/10',
      border: 'border-amber-500/30',
    },
    {
      id: 'sync-companies',
      group: 'Claims',
      label: 'Sync Missing Companies',
      desc: 'Auto-create company records for claims missing a matching company',
      endpoint: '/dev/sync-companies',
      color: 'text-blue-400',
      bg: 'bg-blue-400/10',
      border: 'border-blue-500/30',
    },
    {
      id: 'fix-duplicate-report-nos',
      group: 'Claims',
      label: 'Fix Duplicate Report Nos',
      desc: 'Detect claims sharing the same report_no within a tenant and renumber the older one(s) to a unique trailing ID. Notes cascade automatically. Safe to re-run.',
      endpoint: '/dev/fix-duplicate-report-nos',
      color: 'text-fuchsia-400',
      bg: 'bg-fuchsia-400/10',
      border: 'border-fuchsia-500/30',
    },
    {
      id: 'backfill-claim-db-id',
      group: 'Claims',
      label: 'Backfill Claim db_id + Lock Index',
      desc: 'One-time migration: extract trailing-id into a typed db_id field on every claim and create the unique (tenant_id, db_id) index. Makes future trailing-id collisions structurally impossible at the DB layer. Run AFTER "Fix Duplicate Report Nos".',
      endpoint: '/dev/backfill-claim-db-id',
      color: 'text-violet-400',
      bg: 'bg-violet-400/10',
      border: 'border-violet-500/30',
    },
    {
      id: 'backfill-claim-org-ids',
      group: 'Claims',
      label: 'Backfill Claim org_id from Company',
      desc: 'Fixes "Unassigned" claims on the provider dashboard. Scans every claim with a null/missing org_id and inherits the org_id from its company (case-insensitive name match within the same tenant). Safe to re-run — claims already linked to an org are skipped. Use this after assigning legacy/CSV-imported companies to organizations.',
      endpoint: '/dev/backfill-claim-org-ids',
      color: 'text-indigo-400',
      bg: 'bg-indigo-400/10',
      border: 'border-indigo-500/30',
    },
    {
      id: 'migrate-flat-type-data',
      group: 'Claims',
      label: 'Migrate Flat Fields → type_data',
      desc: 'One-time migration: merge legacy flat fields (al_claims_no, al_submittal, al_adjuster, etc.) on imported claims into the nested type_data structure so the edit side panel reads them. Non-destructive — nested values always win.',
      endpoint: '/dev/migrate-flat-type-data',
      color: 'text-pink-400',
      bg: 'bg-pink-400/10',
      border: 'border-pink-500/30',
    },
    {
      id: 'fix-orphaned-notes',
      group: 'Notes',
      label: 'Fix Orphaned Notes',
      desc: 'Repair notes whose report_no no longer matches a claim (caused by historical rename bug). Remaps by trailing DB ID.',
      endpoint: '/dev/fix-orphaned-notes',
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10',
      border: 'border-emerald-500/30',
    },
    {
      id: 'fix-stripe-customer-emails',
      group: 'Stripe',
      label: 'Fix Stripe Customer Emails',
      desc: 'Correct every tenant\'s Stripe Customer email to the tenant owner (prevents operator Link prefill / wrong SMS OTP). Safe to re-run.',
      endpoint: '/dev/fix-stripe-customer-emails',
      color: 'text-amber-400',
      bg: 'bg-amber-400/10',
      border: 'border-amber-500/30',
    },
    {
      id: 'billing-recompute-noproration',
      group: 'Billing',
      label: 'Recompute Billing (No Prorations)',
      desc: 'Audit every active/trialing tenant; push correct add-ons to Stripe. Charges are queued for the next invoice cycle, no immediate adjustments. Safe to re-run.',
      endpoint: '/dev/billing-recompute',
      body: { proration_behavior: 'none' },
      color: 'text-cyan-400',
      bg: 'bg-cyan-400/10',
      border: 'border-cyan-500/30',
    },
    {
      id: 'billing-recompute-prorate',
      group: 'Billing',
      label: 'Recompute Billing (With Prorations)',
      desc: 'Same as above but Stripe immediately bills/refunds the proration delta. Use to recover revenue from undercharged tenants.',
      endpoint: '/dev/billing-recompute',
      body: { proration_behavior: 'create_prorations' },
      color: 'text-rose-400',
      bg: 'bg-rose-400/10',
      border: 'border-rose-500/30',
    },
    {
      id: 'billing-retry-failures',
      group: 'Billing',
      label: 'Retry Failed Billing Syncs',
      desc: 'Replay every queued billing sync failure (Stripe API errors, rate limits, etc.). Idempotent.',
      endpoint: '/dev/billing-sync-failures/retry-all',
      color: 'text-orange-400',
      bg: 'bg-orange-400/10',
      border: 'border-orange-500/30',
    },
    {
      id: 'grandfather-user-access',
      group: 'Users',
      label: 'Grandfather Provider User Access',
      desc: 'One-time migration: for each Provider tenant, freeze every non-admin user\'s org_access to the tenant\'s current org set. Run BEFORE turning on scoped assignments. Idempotent — skips users that already have access explicitly set.',
      endpoint: '/dev/grandfather-provider-user-access',
      color: 'text-teal-400',
      bg: 'bg-teal-400/10',
      border: 'border-teal-500/30',
    },
    {
      id: 'backfill-geo',
      group: 'Analytics',
      label: 'Backfill Geo for Logins & Demo',
      desc: 'Re-run IPinfo lookups for audit_logs (user.login) and demo_analytics rows that were written before geolocation was wired into the auth flow. Skips private/loopback IPs. Safe to re-run — uses the geo_cache to avoid duplicate IPinfo calls.',
      endpoint: '/dev/backfill-geo',
      color: 'text-sky-400',
      bg: 'bg-sky-400/10',
      border: 'border-sky-500/30',
    },
  ];

  const run = async (action) => {
    setRunning(action.id);
    setResults((prev) => ({ ...prev, [action.id]: null }));
    try {
      const res = await axios.post(`${API}${action.endpoint}`, action.body || {}, { withCredentials: true });
      setResults((prev) => ({ ...prev, [action.id]: res.data }));
      toast.success(res.data.message || 'Done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setRunning(null);
  };

  const loadOrphans = async () => {
    setLoadingOrphans(true);
    setShowOrphans(true);
    try {
      const res = await axios.get(`${API}/dev/orphaned-notes`, { withCredentials: true });
      setOrphans(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load orphaned notes');
      setOrphans({ count: 0, orphans: [] });
    }
    setLoadingOrphans(false);
  };

  // Group actions by 'group' field, preserving insertion order
  const groupedActions = actions.reduce((acc, a) => {
    const g = a.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(a);
    return acc;
  }, {});
  const groupOrder = ['Claims', 'Notes', 'Users', 'Billing', 'Stripe', 'Analytics'].filter((g) => groupedActions[g]);
  // Append any unknown groups at end
  Object.keys(groupedActions).forEach((g) => {
    if (!groupOrder.includes(g)) groupOrder.push(g);
  });

  const GROUP_META = {
    Claims:    { color: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-500/30' },
    Notes:     { color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-500/30' },
    Users:     { color: 'text-teal-400',    bg: 'bg-teal-400/10',    border: 'border-teal-500/30' },
    Billing:   { color: 'text-cyan-400',    bg: 'bg-cyan-400/10',    border: 'border-cyan-500/30' },
    Stripe:    { color: 'text-violet-400',  bg: 'bg-violet-400/10',  border: 'border-violet-500/30' },
    Analytics: { color: 'text-sky-400',     bg: 'bg-sky-400/10',     border: 'border-sky-500/30' },
  };

  // First group expanded by default; rest collapsed
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    groupOrder.forEach((g, idx) => { init[g] = idx === 0; });
    return init;
  });

  const toggleGroup = (g) => setOpenGroups((prev) => ({ ...prev, [g]: !prev[g] }));

  // Whole "Data Maintenance" section is collapsed by default to keep the
  // dashboard tidy. User clicks the header to reveal the grouped accordions.
  const [sectionOpen, setSectionOpen] = useState(false);

  const totalActionCount = actions.length;

  const renderOrphanInspector = () => (
    <div className="px-5 py-4" data-testid="orphan-inspector">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-sky-400">View Unmatched / Orphaned Notes</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Show every note whose <code className="text-slate-400">report_no</code> doesn't match any active claim. Useful before/after running the fix.
          </p>
        </div>
        <button
          onClick={loadOrphans}
          disabled={loadingOrphans}
          className="shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-colors bg-sky-400/10 text-sky-400 border border-sky-500/30 hover:brightness-125 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="view-orphans-btn"
        >
          {loadingOrphans ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Eye className="w-3.5 h-3.5" /> View Unmatched</>}
        </button>
      </div>

      {showOrphans && orphans && (
        <div className="mt-4 border border-slate-800 rounded-lg overflow-hidden" data-testid="orphan-list">
          <div className="px-3 py-2 bg-slate-800/50 text-xs text-slate-300 flex items-center justify-between">
            <span>{orphans.count} orphaned note(s)</span>
            {orphans.count > 0 && (
              <button onClick={() => setShowOrphans(false)} className="text-slate-500 hover:text-slate-300">Hide</button>
            )}
          </div>
          {orphans.count === 0 ? (
            <div className="px-4 py-6 text-xs text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> No orphaned notes found — everything is linked.
            </div>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-900 sticky top-0">
                  <tr className="text-slate-400 text-left">
                    <th className="px-3 py-2 font-medium">Note ID</th>
                    <th className="px-3 py-2 font-medium">Report No</th>
                    <th className="px-3 py-2 font-medium">Loss</th>
                    <th className="px-3 py-2 font-medium">Text</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">By</th>
                    <th className="px-3 py-2 font-medium w-[260px]">Reassign To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {orphans.orphans.map((n) => (
                    <OrphanRow
                      key={n.note_id}
                      note={n}
                      onReassigned={() => loadOrphans()}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAction = (a) => {
    const result = results[a.id];
    const isRunning = running === a.id;
    return (
      <div key={a.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`maintenance-${a.id}`}>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${a.color}`}>{a.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{a.desc}</p>
          {result && (
            <p className="text-xs text-emerald-400 mt-1.5 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {result.message}
            </p>
          )}
          {a.id === 'fix-orphaned-notes' && result && result.unmatched > 0 && (
            <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {result.unmatched} note(s) still unmatched — click "View Unmatched" below.
            </p>
          )}
        </div>
        <button
          onClick={() => run(a)}
          disabled={isRunning}
          className={`shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${a.bg} ${a.color} border ${a.border} hover:brightness-125 disabled:opacity-50`}
          data-testid={`run-${a.id}`}
        >
          {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Run'}
        </button>
      </div>
    );
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg" data-testid="data-maintenance-section">
      <button
        type="button"
        onClick={() => setSectionOpen((v) => !v)}
        className="w-full px-5 py-4 border-b border-slate-800 flex items-center justify-between gap-3 hover:bg-slate-800/40 transition-colors"
        data-testid="data-maintenance-toggle"
        aria-expanded={sectionOpen}
      >
        <div className="flex items-center gap-2 min-w-0">
          {sectionOpen
            ? <ChevronDown className="w-4 h-4 text-amber-400" />
            : <ChevronRight className="w-4 h-4 text-amber-400" />}
          <Wrench className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-white">Data Maintenance</h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-500/30">
            {totalActionCount}
          </span>
        </div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">
          {sectionOpen ? 'Hide' : 'Show'}
        </span>
      </button>
      {sectionOpen && (
        <div className="divide-y divide-slate-800">
          {groupOrder.map((g) => {
            const meta = GROUP_META[g] || { color: 'text-slate-300', bg: 'bg-slate-700/30', border: 'border-slate-600/30' };
            const items = groupedActions[g];
            const isOpen = !!openGroups[g];
            const isNotes = g === 'Notes';
            return (
              <div key={g} data-testid={`group-${g.toLowerCase()}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(g)}
                  className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-800/40 transition-colors"
                  data-testid={`group-toggle-${g.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen
                      ? <ChevronDown className={`w-4 h-4 ${meta.color}`} />
                      : <ChevronRight className={`w-4 h-4 ${meta.color}`} />}
                    <span className={`text-sm font-semibold ${meta.color}`}>{g}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.color} border ${meta.border}`}>
                      {isNotes ? items.length + 1 : items.length}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {isOpen ? 'Hide' : 'Show'}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-800 divide-y divide-slate-800 bg-slate-950/30">
                    {items.map(renderAction)}
                    {isNotes && renderOrphanInspector()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrphanRow({ note, onReassigned }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const q = search.trim();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (note.tenant_id) params.set('tenant_id', note.tenant_id);
        params.set('limit', '20');
        const res = await axios.get(`${API}/dev/claims-lookup?${params.toString()}`, { withCredentials: true });
        setResults(res.data?.claims || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, note.tenant_id]);

  const reassign = async (targetReportNo) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/dev/orphaned-notes/reassign`, {
        note_id: note.note_id,
        target_report_no: targetReportNo,
      }, { withCredentials: true });
      toast.success(res.data?.message || 'Reassigned');
      setOpen(false);
      onReassigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reassign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="text-slate-300 hover:bg-slate-800/30" data-testid={`orphan-row-${note.note_id}`}>
      <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{note.note_id}</td>
      <td className="px-3 py-2 font-mono text-amber-400">{note.report_no}</td>
      <td className="px-3 py-2 uppercase text-slate-400">{note.loss_type || '-'}</td>
      <td className="px-3 py-2 max-w-xs truncate" title={note.text_preview}>{note.text_preview || '-'}</td>
      <td className="px-3 py-2 text-slate-500">{note.created_at ? new Date(note.created_at).toLocaleDateString() : '-'}</td>
      <td className="px-3 py-2 text-slate-400">{note.created_by_name || '-'}</td>
      <td className="px-3 py-2 relative">
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            className="px-2 py-1 rounded-md text-[11px] bg-emerald-400/10 text-emerald-400 border border-emerald-500/30 hover:brightness-125"
            data-testid={`reassign-open-${note.note_id}`}
          >
            Reassign...
          </button>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search claim, company, driver..."
                  className="w-full pl-7 pr-2 py-1 text-[11px] bg-slate-950 border border-slate-700 rounded-md text-slate-200 placeholder-slate-600 focus:outline-none focus:border-emerald-500"
                  data-testid={`reassign-search-${note.note_id}`}
                />
              </div>
              <button
                onClick={() => { setOpen(false); setSearch(''); }}
                className="p-1 text-slate-500 hover:text-slate-300"
                data-testid={`reassign-cancel-${note.note_id}`}
                aria-label="Cancel"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="max-h-44 overflow-auto rounded-md border border-slate-800 bg-slate-950">
              {loading ? (
                <div className="px-2 py-2 text-[11px] text-slate-500 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                </div>
              ) : results.length === 0 ? (
                <div className="px-2 py-2 text-[11px] text-slate-500">No matching claims</div>
              ) : (
                results.map((c) => (
                  <button
                    key={c.report_no}
                    disabled={busy}
                    onClick={() => reassign(c.report_no)}
                    className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-emerald-500/10 border-b border-slate-800 last:border-b-0 disabled:opacity-50"
                    data-testid={`reassign-pick-${note.note_id}-${c.report_no}`}
                  >
                    <div className="font-mono text-emerald-400 truncate">{c.report_no}</div>
                    <div className="text-slate-500 truncate">
                      {c.company_name || '—'}{c.drivers_name ? ` · ${c.drivers_name}` : ''}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

